import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import {
  Shield,
  ShieldCheck,
  Lock,
  Link2,
  Hash,
  CheckCircle2,
  Circle,
  Play,
  Pause,
  RotateCcw,
  Zap,
  Database,
  FileCheck,
  Users,
  AlertTriangle,
  Clock,
  TrendingUp,
  Eye,
  Fingerprint,
  Award,
  Building2,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ComplianceMetrics } from '@/types/compliance';

interface INDRAShowcaseProps {
  metrics?: ComplianceMetrics;
  onVerifyIntegrity?: () => void;
  isVerifying?: boolean;
}

const highlights = [
  {
    icon: Link2,
    title: 'Hash Chain Inmutable',
    description: 'Cadena criptografica SHA-256 tipo blockchain',
    detail: 'Cada registro enlazado al anterior, imposible de alterar sin deteccion',
    color: 'text-blue-500',
    bg: 'bg-blue-50',
  },
  {
    icon: Eye,
    title: 'Auditoria Automatica',
    description: 'Captura todas las operaciones criticas',
    detail: '10 tablas monitorizadas con triggers en tiempo real',
    color: 'text-green-500',
    bg: 'bg-green-50',
  },
  {
    icon: AlertTriangle,
    title: 'Deteccion de Anomalias',
    description: 'Alertas automaticas por comportamiento sospechoso',
    detail: 'Operaciones masivas, acceso fuera de horario, acciones criticas',
    color: 'text-orange-500',
    bg: 'bg-orange-50',
  },
  {
    icon: Fingerprint,
    title: 'Trazabilidad Completa',
    description: 'Quien, que, cuando y desde donde',
    detail: 'Usuario, IP, sesion, rol, timestamp UTC',
    color: 'text-purple-500',
    bg: 'bg-purple-50',
  },
];

const standards = [
  { name: 'ISO 27001', coverage: 85, icon: Shield },
  { name: 'ENS', coverage: 80, icon: Building2 },
  { name: 'CMMI 3', coverage: 75, icon: Award },
  { name: 'eIDAS', coverage: 60, icon: FileCheck },
];

export function INDRAShowcase({ metrics, onVerifyIntegrity, isVerifying }: INDRAShowcaseProps) {
  const [activeHighlight, setActiveHighlight] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [showBlockchain, setShowBlockchain] = useState(false);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setActiveHighlight((prev) => (prev + 1) % highlights.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const chainBlocks = [
    { seq: 1, hash: 'a7f3c9...', type: 'GENESIS' },
    { seq: 2, hash: '3b8d21...', type: 'work_orders' },
    { seq: 3, hash: 'f92e4a...', type: 'quality_controls' },
    { seq: 4, hash: '1c7b85...', type: 'process_records' },
    { seq: 5, hash: '8d4f6e...', type: 'legal_documents' },
  ];

  return (
    <div className="space-y-6">
      {/* Header Hero */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-6">
        <div className="absolute inset-0 bg-grid-white/5" />
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-500/20 rounded-full blur-3xl" />

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
              <Shield className="w-7 h-7" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Athenea Compliance</h2>
              <p className="text-white/70 text-sm">Sistema de Auditoria y Cumplimiento Normativo</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-center">
              <p className="text-3xl font-bold">{metrics?.audit_metrics.total_records?.toLocaleString() || '---'}</p>
              <p className="text-xs text-white/70">Registros Auditados</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-green-400">
                {metrics?.integrity_status.chain_valid ? '100%' : '---'}
              </p>
              <p className="text-xs text-white/70">Integridad</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-center">
              <p className="text-3xl font-bold">{metrics?.alert_metrics.open_alerts || 0}</p>
              <p className="text-xs text-white/70">Alertas Activas</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-center">
              <p className="text-3xl font-bold">10</p>
              <p className="text-xs text-white/70">Tablas Monitorizadas</p>
            </div>
          </div>
        </div>
      </div>

      {/* Blockchain Visualization */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">Cadena de Integridad (Hash Chain)</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowBlockchain(!showBlockchain)}
              >
                {showBlockchain ? 'Ocultar' : 'Visualizar'} Cadena
              </Button>
              <Button
                size="sm"
                onClick={onVerifyIntegrity}
                disabled={isVerifying}
              >
                {isVerifying ? (
                  <RotateCcw className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <ShieldCheck className="h-4 w-4 mr-1" />
                )}
                Verificar
              </Button>
            </div>
          </div>
          <CardDescription>
            Cada registro contiene el hash SHA-256 del anterior, creando una cadena inmutable
          </CardDescription>
        </CardHeader>

        {showBlockchain && (
          <CardContent>
            <div className="flex items-center gap-2 overflow-x-auto pb-4">
              {chainBlocks.map((block, idx) => (
                <div key={block.seq} className="flex items-center">
                  <div className={cn(
                    "flex-shrink-0 w-32 p-3 rounded-lg border-2 transition-all",
                    idx === 0 ? "border-blue-500 bg-blue-50" : "border-gray-200 bg-gray-50",
                    "hover:border-primary hover:shadow-md"
                  )}>
                    <div className="flex items-center justify-between mb-1">
                      <Badge variant="outline" className="text-xs">#{block.seq}</Badge>
                      {idx === 0 && <Sparkles className="h-3 w-3 text-blue-500" />}
                    </div>
                    <p className="font-mono text-xs text-muted-foreground truncate">
                      {block.hash}
                    </p>
                    <p className="text-xs mt-1 truncate">{block.type}</p>
                  </div>
                  {idx < chainBlocks.length - 1 && (
                    <ArrowRight className="h-4 w-4 text-muted-foreground mx-1 flex-shrink-0" />
                  )}
                </div>
              ))}
              <div className="flex-shrink-0 w-24 h-20 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
                <span className="text-xs text-muted-foreground">+{(metrics?.audit_metrics.total_records || 0) - 5}</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              <Lock className="h-3 w-3 inline mr-1" />
              Si un registro se modifica, el hash cambia y la cadena se rompe - deteccion inmediata de manipulacion
            </p>
          </CardContent>
        )}
      </Card>

      {/* Highlights Carousel */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Zap className="h-5 w-5 text-yellow-500" />
              Capacidades Clave
            </CardTitle>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setIsAutoPlaying(!isAutoPlaying)}
              >
                {isAutoPlaying ? (
                  <Pause className="h-4 w-4" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-2 mb-4">
            {highlights.map((h, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setActiveHighlight(idx);
                  setIsAutoPlaying(false);
                }}
                className={cn(
                  "p-2 rounded-lg text-left transition-all",
                  idx === activeHighlight
                    ? "bg-primary text-white"
                    : "bg-muted hover:bg-muted/80"
                )}
              >
                <h.icon className={cn("h-5 w-5 mb-1", idx === activeHighlight ? "text-white" : h.color)} />
                <p className="text-xs font-medium truncate">{h.title}</p>
              </button>
            ))}
          </div>

          <div className={cn("p-4 rounded-lg", highlights[activeHighlight].bg)}>
            <div className="flex items-start gap-3">
              {(() => {
                const Icon = highlights[activeHighlight].icon;
                return <Icon className={cn("h-8 w-8", highlights[activeHighlight].color)} />;
              })()}
              <div>
                <h4 className="font-semibold text-lg">{highlights[activeHighlight].title}</h4>
                <p className="text-muted-foreground">{highlights[activeHighlight].description}</p>
                <p className="text-sm mt-2 font-medium">{highlights[activeHighlight].detail}</p>
              </div>
            </div>
          </div>

          <div className="flex justify-center gap-1.5 mt-4">
            {highlights.map((_, idx) => (
              <div
                key={idx}
                className={cn(
                  "h-1.5 rounded-full transition-all",
                  idx === activeHighlight ? "w-6 bg-primary" : "w-1.5 bg-muted"
                )}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Standards Progress */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Award className="h-5 w-5 text-primary" />
            Cumplimiento de Estandares
          </CardTitle>
          <CardDescription>
            Evaluacion de conformidad con marcos normativos requeridos por INDRA
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {standards.map((std) => (
              <div key={std.name} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <std.icon className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{std.name}</span>
                  </div>
                  <span className={cn(
                    "font-bold",
                    std.coverage >= 80 ? "text-green-600" : std.coverage >= 60 ? "text-yellow-600" : "text-red-600"
                  )}>
                    {std.coverage}%
                  </span>
                </div>
                <div className="relative">
                  <Progress value={std.coverage} className="h-3" />
                  <div
                    className="absolute top-0 left-0 h-3 rounded-full bg-gradient-to-r from-green-500/20 to-transparent"
                    style={{ width: `${std.coverage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>

          <Separator className="my-4" />

          <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-500" />
              <span className="font-medium">Cumplimiento Global</span>
            </div>
            <span className="text-2xl font-bold text-primary">
              {Math.round(standards.reduce((s, std) => s + std.coverage, 0) / standards.length)}%
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Demo Checklist */}
      <Card className="border-primary/50 bg-primary/5">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-primary" />
            Puntos para Demo INDRA
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              'Mostrar verificacion de integridad en tiempo real',
              'Demostrar que los registros son inmutables',
              'Crear una operacion y ver el log generado',
              'Mostrar deteccion automatica de anomalias',
              'Explicar la cadena de hashes SHA-256',
              'Mostrar alertas y su gestion',
              'Exportar reporte PDF de compliance',
              'Navegar el historial de auditoria con filtros',
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm">
                <Circle className="h-4 w-4 text-primary flex-shrink-0" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
